// src/components/admin/ThemeItem.js
import { useState } from 'react';
import useAuth from '../../hooks/useAuth';
import api from '../../services/api';
import AddPhraseForm from './AddPhraseForm';
import PhraseItem from './PhraseItem';

const ThemeItem = ({
  theme,
  projectId,
  onUpdate,
  onDelete,
  isExpanded,        // ← получаем из родителя
  onToggleExpand     // ← получаем из родителя
}) => {
  const { user } = useAuth();

  // Локальное состояние для форм
  const [name, setName] = useState(theme.name);
  const [threshold, setThreshold] = useState(theme.threshold);
  const [absolutelyEverything, setAbsolutelyEverything] = useState(!!theme.absolutely_everything);
  const [positive, setPositive] = useState(theme.positive !== false);
  const roleNames = new Set(user?.roles?.map(r => r.name) || []);
  // Переименовать тему
  const handleRename = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/analytics/theme/${theme.id}`, { name });
      setName(name);
      onUpdate(); // Перезагружаем данные с сервера
    } catch (err) {
      console.error('Ошибка при переименовании темы:', err);
      window.showToast('Не удалось переименовать тему');
    }
  };

  // Обновить порог
  const handleUpdateThreshold = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/analytics/theme/${theme.id}`, { threshold });
      onUpdate();
    } catch (err) {
      console.error('Ошибка при обновлении порога:', err);
      window.showToast('Не удалось обновить порог');
    }
  };

  // Обновить тип совпадения
  const handleUpdateMatchType = async (e) => {
    const value = e.target.value === 'true';
    setAbsolutelyEverything(value);
    try {
      await api.patch(`/admin/analytics/theme/${theme.id}`, { absolutely_everything: value });
      onUpdate();
    } catch (err) {
      console.error('Ошибка при обновлении типа совпадения:', err);
      setAbsolutelyEverything(theme.absolutely_everything);
      window.showToast('Не удалось обновить тип совпадения');
    }
  };

  // Обновить позитивность
  const handleUpdatePositive = async (e) => {
  const value = e.target.value === 'true';
  setPositive(value);
  try {
    await api.patch(`/admin/analytics/theme/${theme.id}`, {
      positive: value  // ✅ Отправляем true/false, а не null/false
    });
    onUpdate();
  } catch (err) {
    console.error('Ошибка при обновлении позитивности:', err);
    setPositive(theme.positive !== false);
    window.showToast('Не удалось обновить позитивность');
  }
};

  // Удалить тему
  const handleDelete = async () => {
    if (!window.confirm('Удалить тему?')) return;
    try {
      await api.delete(`/admin/analytics/theme/${theme.id}`);
      onDelete(theme.id);
    } catch (err) {
      console.error('Ошибка при удалении темы:', err);
      window.showToast('Не удалось удалить тему');
    }
  };

  // Определение типа темы
  const isPositive = theme.positive !== false;

  return (
    <li className="list-group-item d-flex flex-column gap-2" data-theme-id={theme.id}>
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <h3 style={{ marginBottom: '0.25rem' }}>
            {/* Кнопка сворачивания */}
            <button
              type="button"
              className="btn btn-link p-0 me-1"
              style={{ textDecoration: 'none', color: 'inherit' }}
              onClick={onToggleExpand} // ← вызываем внешний обработчик
              aria-label={isExpanded ? 'Свернуть' : 'Развернуть'}
            >
              {isExpanded ? '▼' : '▶'}
            </button>
            Тема: {theme.name}
          </h3>
          <small className="text-muted">
            Порог: {theme.threshold | 0}% | {isPositive ? '✅ Позитивная' : '❌ Негативная'}
          </small>
        </div>
        <div>
          {roleNames.has('admin') && (
            <>
              {/* Форма: изменить название */}
              <form onSubmit={handleRename} style={{ display: 'inline' }}>
                <input
                  type="text"
                  className="form-control me-2"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  style={{ width: '200px', display: 'inline-block' }}
                />
                <button type="submit" className="btn btn-sm btn-success ms-2">
                  Изменить название
                </button>
              </form>

              {/* Форма: обновить порог */}
              <form onSubmit={handleUpdateThreshold} style={{ display: 'inline' }}>
                <select
                  className="form-select form-select-sm d-inline-block w-auto"
                  value={threshold}
                  onChange={(e) => setThreshold(Number(e.target.value))}
                >
                  {[50, 60, 70, 80, 90, 100].map((val) => (
                    <option key={val} value={val}>
                      {val}%
                    </option>
                  ))}
                </select>
                <button type="submit" className="btn btn-sm btn-info ms-2">
                  Обновить порог
                </button>
              </form>

              {/* Тип совпадения */}
              <select
                className="form-select form-select-sm d-inline-block w-auto"
                value={absolutelyEverything}
                onChange={handleUpdateMatchType}
              >
                <option value={false}>Хотя бы одна</option>
                <option value={true}>Все фразы</option>
              </select>

              {/* Позитивность */}
              <select
                className="form-select form-select-sm d-inline-block w-auto"
                value={positive}
                onChange={handleUpdatePositive}
              >
                <option value={true}>✅ Да / Не задано</option>
                <option value={false}>❌ Нет (негативная)</option>
              </select>

              {/* Удалить тему */}
              <button type="button" className="btn btn-sm btn-danger ms-2" onClick={handleDelete}>
                Удалить
              </button>
            </>
          )}
        </div>
      </div>

      {/* Блок фраз — отображается только если isExpanded = true */}
      {isExpanded && (
        <div className="card border-0 mt-3">
          <div className="card-body">
            <h6 className="card-title">Фразы:</h6>

            {roleNames.has('admin') && (
              <AddPhraseForm
                themeId={theme.id}
                projectId={projectId}
                absolutelyEverything={absolutelyEverything}
                onAdd={onUpdate}
              />
            )}

            <ul className="list-unstyled ms-4" id={`phrases-list-${theme.id}`}>
              {theme.phrases?.map((phrase) => (
                <PhraseItem
                  key={phrase.id}
                  phrase={phrase}
                  theme={theme}
                  projectId={projectId}
                  onEdit={onUpdate}
                  onDelete={onUpdate}
                />
              ))}
            </ul>
          </div>
        </div>
      )}
    </li>
  );
};

export default ThemeItem;

